import { GROUND } from '../core/config';
import { createArchetype, key, MATERIALS, type Building, type City, type RoadEdge, type Place } from './city';
import { roadSegment } from './roads';
import type { LandBounds } from './land-area';
import { furnish } from './furniture';
import type { StrategicTransportLayout } from './strategic-transport';

export interface StrategicParcel extends LandBounds {
  id: 'military' | 'power';
  name: string;
  buildings: number[];
  roads: number[];
  gate: { x: number; y: number; z: number };
}
export interface StrategicDistricts {
  transport?: StrategicTransportLayout;
  fences: number[];
  parcels: StrategicParcel[];
  barracks: number[];
  garages: number[];
  armoury: number;
  command: number;
  clinic: number;
  hangars: number[];
  parade: { x: number; y: number; z: number }[];
  plant: {
    halls: number[];
    cooling: number[];
    control: number;
    switchyard: number[];
    duties?: { building: number; places: Place[] }[];
  };
}

/** Original bounded campuses, with physical roads and individually damageable
 * buildings. Operational crews/power/security are separate shared controllers. */
export function prepareStrategicDistricts(city: City): City {
  if (city.strategic) return city;
  const graph = city.roadGraph!;
  let serial =
    Math.max(
      ...[
        ...city.buildings,
        ...(city.props ?? []),
        ...(city.infrastructure ?? []),
        ...(city.bridges ?? []),
      ].map((b) => b.id),
    ) + 1;
  const parcels: StrategicParcel[] = [
    {
      id: 'military',
      name: 'Alder Garrison',
      minX: -1030,
      maxX: -645,
      minZ: -650,
      maxZ: 390,
      buildings: [],
      roads: [],
      gate: { x: -655, y: GROUND + 0.2, z: 160 },
    },
    {
      id: 'power',
      name: 'East Cape Power Station',
      minX: 645,
      maxX: 1100,
      minZ: -250,
      maxZ: 610,
      buildings: [],
      roads: [],
      gate: { x: 655, y: GROUND + 0.2, z: 160 },
    },
  ];
  const military = parcels[0],
    power = parcels[1];
  const layout: StrategicDistricts = {
    fences: [],
    parcels,
    barracks: [],
    garages: [],
    armoury: -1,
    command: -1,
    clinic: -1,
    hangars: [],
    parade: [],
    plant: { halls: [], cooling: [], control: -1, switchyard: [] },
  };
  city.strategic = layout;
  city.districts ??= [];
  city.props ??= [];
  city.infrastructure ??= [];
  for (const p of parcels)
    city.districts.push({
      id: p.id,
      name: p.name,
      x: (p.minX + p.maxX) / 2,
      z: (p.minZ + p.maxZ) / 2,
      radius: Math.hypot(p.maxX - p.minX, p.maxZ - p.minZ) / 2,
    });
  const install = (b: Building, p: StrategicParcel, prop = false) => {
    b.district = p.id;
    b.chunk = `${Math.floor(b.x / (city.chunkSize ?? 60))},${Math.floor(b.z / (city.chunkSize ?? 60))}`;
    (prop ? city.props! : city.buildings).push(b);
    p.buildings.push(b.id);
    return b;
  };
  const shell = (
    p: StrategicParcel,
    x: number,
    z: number,
    kind: Parameters<typeof createArchetype>[3],
    w: number,
    d: number,
    floors: number,
    name: string,
  ) => {
    const b = createArchetype(serial++, x, z, kind, w, d, floors);
    b.name = name;
    b.accent = p.id === 'military' ? 0x576c48 : 0x4c7f89;
    for (const c of b.cells)
      if (c.material !== 'glass' && c.role === 'facade') c.color = p.id === 'military' ? 0xb4b49a : 0xcbd1ca;
    if (kind === 'hangar')
      for (const c of b.cells) {
        if ((c.role === 'roof' && c.z % 4 === 0) || (c.z === 0 && c.y >= 5) || c.role === 'column') {
          c.role = c.role === 'column' ? 'column' : 'beam';
          c.capacity = 1800 * 4;
          c.color = 0x60746d;
        }
      }
    return install(b, p);
  };
  const detail = (
    p: StrategicParcel,
    x: number,
    z: number,
    w: number,
    d: number,
    h: number,
    name: string,
  ) => {
    const b: Building = {
      id: serial++,
      name,
      kind: 'furniture',
      category: 'prop',
      exactGeometry: true,
      x,
      z,
      w,
      d,
      h,
      color: 0x718376,
      accent: 0xe8dbb5,
      cells: [],
    };
    return install(b, p, true);
  };
  const cell = (
    b: Building,
    x: number,
    y: number,
    z: number,
    material: Building['cells'][number]['material'],
    role: NonNullable<Building['cells'][number]['role']>,
    color: number,
    bounds?: Building['cells'][number]['bounds'],
  ) => {
    const c = {
      id: b.id * 1e6 + key(x, y, z),
      x,
      y,
      z,
      material,
      role,
      color,
      hp: MATERIALS[material].strength,
      root: y === 0,
      bounds,
    };
    b.cells.push(c);
    return c;
  };
  const node = (x: number, z: number) => {
    const existing = graph.nodes.find((n) => Math.abs(n.x - x) < 0.01 && Math.abs(n.z - z) < 0.01);
    if (existing) return existing.id;
    const id = graph.nodes.length;
    graph.nodes.push({ id, x, z, y: GROUND + 0.2, layer: 'street' });
    return id;
  };
  const newNodes = new Set<number>();
  const road = (p: StrategicParcel, x: number, z: number, X: number, Z: number) => {
    const startLength = graph.nodes.length,
      a = node(x, z),
      b = node(X, Z);
    for (let id = startLength; id < graph.nodes.length; id++) newNodes.add(id);
    if (graph.edges.some((e) => (e.from === a && e.to === b) || (e.from === b && e.to === a))) return;
    const A = graph.nodes[a],
      edge: RoadEdge = {
        id: graph.edges.length,
        from: a,
        to: b,
        width: 9.5,
        kind: `${p.name} service avenue`,
        restricted: p.id === 'military',
        layer: 'street',
        pavementWidth: 1.5,
        maxGrade: 0,
        clearance: 8,
        surfaceIds: [],
        profile: [
          { x, y: GROUND + 0.2, z },
          { x: X, y: GROUND + 0.2, z: Z },
        ],
      };
    graph.edges.push(edge);
    p.roads.push(edge.id);
    const length = Math.hypot(X - x, Z - z),
      dx = (X - x) / length,
      dz = (Z - z) / length,
      r = 6.25;
    const count = Math.ceil((length - r * 2) / 30);
    for (let i = 0; i < count; i++) {
      const t0 = r + ((length - r * 2) * i) / count,
        t1 = r + ((length - r * 2) * (i + 1)) / count;
      const surface = roadSegment(
        serial++,
        edge,
        { x: A.x + dx * t0, y: GROUND + 0.2, z: A.z + dz * t0 },
        { x: A.x + dx * t1, y: GROUND + 0.2, z: A.z + dz * t1 },
        r,
      );
      city.infrastructure!.push(surface);
      edge.surfaceIds!.push(surface.id);
    }
  };
  // A parallel perimeter and internal grid provides several patrol circuits,
  // not a single line of forty vehicles. Both campuses join existing roads.
  for (const p of parcels) {
    const xs = p.id === 'military' ? [-975, -830, -690] : [690, 855, 1020];
    const zs = p.id === 'military' ? [-585, -410, -235, -60, 115, 290] : [-185, -10, 160, 335, 510];
    for (const x of xs)
      for (let i = 1; i < zs.length; i++) {
        if (p.id === 'military' && x === -975 && i === 1) {
          road(p, -900, zs[0], -900, zs[1]);
        } else if (p.id === 'military' && x === -690 && zs[i] === 290) {
          road(p, x, 115, x, 160);
          road(p, x, 160, x, 290);
        } else road(p, x, zs[i - 1], x, zs[i]);
      }
    for (const z of zs)
      for (let i = 1; i < xs.length; i++) {
        if (p.id === 'military' && i === 1 && z === -585) road(p, -900, z, xs[i], z);
        else if (p.id === 'military' && i === 1 && z === -410) {
          road(p, xs[0], z, -900, z);
          road(p, -900, z, xs[i], z);
        } else road(p, xs[i - 1], z, xs[i], z);
      }
    road(p, p.id === 'military' ? -690 : 690, 160, p.id === 'military' ? -600 : 620, 160);
  }
  for (const id of newNodes) {
    const n = graph.nodes[id],
      edges = graph.edges.filter((e) => e.from === id || e.to === id);
    const patch = roadSegment(
      serial++,
      { ...edges[0], width: 12.5 },
      { x: n.x - 6.25, y: n.y!, z: n.z },
      { x: n.x + 6.25, y: n.y!, z: n.z },
      6.25,
    );
    patch.name = 'Campus junction';
    city.infrastructure.push(patch);
    for (const e of edges) e.surfaceIds!.push(patch.id);
  }
  for (let row = 0; row < 5; row++)
    for (let col = 0; col < 2; col++) {
      const b = shell(
        military,
        -949 + col * 66,
        [-552, -490, -374, -312, -200][row],
        'modern-apartment',
        28,
        16,
        3,
        `Cedar Barracks ${row * 2 + col + 1}`,
      );
      layout.barracks.push(b.id);
    }
  layout.command = shell(
    military,
    -793,
    -544,
    'glass-office',
    28,
    24,
    3,
    'Garrison Command and Operations',
  ).id;
  layout.armoury = shell(military, -792, -362, 'warehouse', 30, 22, 2, 'Equipment and Armoury').id;
  layout.clinic = shell(
    military,
    -794,
    -20,
    'modern-apartment',
    28,
    24,
    2,
    'Garrison Medical and Relief Centre',
  ).id;
  for (let i = 0; i < 4; i++)
    layout.garages.push(
      shell(
        military,
        -957 + (i % 2) * 74,
        152 + Math.floor(i / 2) * 70,
        'warehouse',
        28,
        22,
        2,
        `Motor Pool Workshop ${i + 1}`,
      ).id,
    );
  for (let i = 0; i < 2; i++)
    layout.hangars.push(
      shell(military, -950 + i * 145, 310, 'hangar', 30, 28, 3, `Air Service Hangar ${i + 1}`).id,
    );
  shell(military, -793, -190, 'warehouse', 28, 22, 2, 'Rations and Relief Logistics');
  for (let x = 0; x < 8; x++)
    for (let z = 0; z < 10; z++) layout.parade.push({ x: -959 + x * 3, y: GROUND + 0.05, z: -30 + z * 2.4 });
  const parade = detail(military, -963, -35, 26, 27, 1, 'Parade and inspection square');
  for (let x = 0; x < 26; x++)
    for (let z = 0; z < 27; z++)
      cell(parade, x, 0, z, 'concrete', 'slab', x % 5 === 0 || z % 5 === 0 ? 0xd8d5b5 : 0x8b9684, {
        x: 0,
        y: 0,
        z: 0,
        w: 1,
        h: 0.035,
        d: 1,
      });
  for (let i = 0; i < 12; i++) {
    const p = detail(
      military,
      -792 + (i % 4) * 21,
      52 + Math.floor(i / 4) * 17,
      5,
      4,
      3,
      `Field equipment shelter ${i + 1}`,
    );
    for (let x = 0; x < 5; x++)
      for (let z = 0; z < 4; z++) {
        cell(p, x, 2, z, 'wood', 'roof', 0x73835a, { x: 0, y: 0.85, z: 0, w: 1, h: 0.15, d: 1 });
        if ((x === 0 || x === 4) && (z === 0 || z === 3))
          for (let y = 0; y < 2; y++)
            cell(p, x, y, z, 'metal', 'column', 0x526c54, { x: 0.38, y: 0, z: 0.38, w: 0.24, h: 1, d: 0.24 });
      }
  }
  layout.plant.control = shell(
    power,
    711,
    -145,
    'glass-office',
    30,
    24,
    4,
    'East Cape Control and Administration',
  ).id;
  shell(power, 905, -148, 'modern-apartment', 28, 20, 3, 'Plant Staff and Safety Centre');
  for (let i = 0; i < 3; i++)
    layout.plant.halls.push(
      shell(power, [715, 785, 911][i], 32, 'sawtooth-factory', 30, 28, 3, `Turbine and Service Hall ${i + 1}`)
        .id,
    );
  shell(power, 712, 395, 'warehouse', 30, 26, 2, 'Plant Maintenance and Stores');
  shell(power, 915, 399, 'warehouse', 30, 26, 2, 'Rail Freight and Equipment Receiving');
  for (const [cx, cz] of [
    [757, 253],
    [939, 253],
  ]) {
    const b: Building = {
      id: serial++,
      name: 'East Cape cooling structure',
      kind: 'grain-silo',
      x: cx - 18.75,
      z: cz - 18.75,
      w: 30,
      d: 30,
      h: 46,
      color: 0xd0d1c7,
      accent: 0x9bada6,
      cells: [],
      exactGeometry: true,
      roofLevel: 45,
    };
    for (let y = 0; y < 46; y++) {
      const radius = 14.5 - 5.2 * Math.sin((Math.PI * y) / 60);
      for (let x = 0; x < 30; x++)
        for (let z = 0; z < 30; z++) {
          const r = Math.hypot(x - 14.5, z - 14.5);
          if (r <= radius && r > radius - 1.15) {
            const reinforced = cell(
              b,
              x,
              y,
              z,
              'concrete',
              y === 0 ? 'column' : y % 8 === 0 ? 'beam' : 'wall',
              y % 8 === 0 ? 0xafbbb0 : 0xd0d1c7,
            );
            // Continuous reinforced shell: capacity supports its own height,
            // while normal concrete hit points and fracture remain unchanged.
            Object.assign(reinforced, { capacity: 660 * 8 });
          }
        }
    }
    install(b, power);
    layout.plant.cooling.push(b.id);
  }
  for (let i = 0; i < 18; i++) {
    const b = detail(
      power,
      715 + (i % 6) * 20,
      535 + Math.floor(i / 6) * 18,
      8,
      6,
      5,
      `Switchyard module ${i + 1}`,
    );
    for (let x = 0; x < 8; x++)
      for (let z = 0; z < 6; z++) {
        cell(b, x, 0, z, 'concrete', 'slab', 0xaeb7a8, { x: 0, y: 0, z: 0, w: 1, h: 0.15, d: 1 });
        if (x >= 2 && x <= 5 && z >= 1 && z <= 4)
          for (let y = 1; y <= 2; y++) cell(b, x, y, z, 'metal', 'prop', 0x789894);
        if (x === 1 || x === 6)
          for (let y = 1; y < 5; y++)
            if (z === 1 || z === 4)
              cell(b, x, y, z, 'metal', 'column', y % 2 ? 0x677a72 : 0xc1c5b4, {
                x: 0.32,
                y: 0,
                z: 0.32,
                w: 0.36,
                h: 1,
                d: 0.36,
              });
      }
    layout.plant.switchyard.push(b.id);
  }
  // Connected pipes stand on repeated grounded brackets; every segment uses
  // occupied cell parts and shares normal fire/fracture handling.
  for (let i = 0; i < 8; i++) {
    const b = detail(power, (i < 4 ? 710 : 890) + (i % 4) * 28, 119, 22, 3, 4, `Service pipe rack ${i + 1}`);
    for (let x = 0; x < 22; x++)
      for (const z of [0, 2]) {
        cell(b, x, 3, z, 'metal', 'beam', 0xa2aaa0, { x: 0, y: 0.2, z: 0.23, w: 1, h: 0.5, d: 0.5 });
        if (x % 6 === 0)
          for (let y = 0; y < 3; y++)
            cell(b, x, y, z, 'metal', 'column', 0x6c8175, { x: 0.35, y: 0, z: 0.35, w: 0.3, h: 1, d: 0.3 });
      }
  }
  for (const p of parcels) {
    // Repeat grounded fence panels with open road gates; no giant invisible wall.
    for (const side of ['west', 'east', 'north', 'south'] as const) {
      const vertical = side === 'west' || side === 'east',
        fixed =
          side === 'west'
            ? p.minX + 22
            : side === 'east'
              ? p.maxX - 22
              : side === 'north'
                ? p.minZ + 22
                : p.maxZ - (p.id === 'military' ? 3 : 22);
      const from = vertical ? p.minZ + 22 : p.minX + 22,
        to = vertical ? p.maxZ - 22 : p.maxX - 22;
      for (let along = from; along < to; along += 10) {
        if (vertical && Math.abs(along - 160) < 18) continue;
        // The actual runway has open approaches. The former south fence ran
        // through its fuselage lane; the fence now borders the strip, with
        // physical openings at each end for the whole aircraft envelope.
        if (p.id === 'military' && vertical && along + 10 > 340) continue;
        const b = detail(
          p,
          vertical ? fixed : along,
          vertical ? along : fixed,
          vertical ? 1 : 8,
          vertical ? 8 : 1,
          3,
          `${p.name} perimeter fence`,
        );
        layout.fences.push(b.id);
        for (let j = 0; j < 8; j++)
          for (let y = 0; y < 3; y++) {
            if (y === 1 && j % 4 !== 0) continue;
            const x = vertical ? 0 : j,
              z = vertical ? j : 0;
            const panelCell = cell(
              b,
              x,
              y,
              z,
              'metal',
              j % 4 === 0 ? 'column' : 'beam',
              p.id === 'military' ? 0x647558 : 0x859793,
              {
                x: vertical ? 0.42 : 0,
                y: j % 4 === 0 ? 0 : 0.8,
                z: vertical ? 0 : 0.42,
                w: vertical ? 0.16 : 1,
                h: j % 4 === 0 ? 1 : 0.2,
                d: vertical ? 1 : 0.16,
              },
            );
            panelCell.root = y === 0 && j % 4 === 0;
          }
      }
    }
    shell(p, p.id === 'military' ? -680 : 656, 177, 'corner-shop', 8, 8, 1, `${p.name} Gatehouse`);
  }
  return city;
}

export function militaryZone(city: City, p: { x: number; z: number }): boolean {
  const b = city.strategic?.parcels.find((p) => p.id === 'military');
  return !!b && p.x > b.minX + 22 && p.x < b.maxX - 22 && p.z > b.minZ + 22 && p.z < b.maxZ - 22;
}

/** Reserve authored working furniture for the original operators; ordinary
 * residents retain all other city homes/jobs instead of occupying army bunks. */
export function prepareStrategicDuties(city: City): City {
  const layout = city.strategic;
  if (!layout || layout.plant.duties) return city;
  const military = new Set(layout.parcels[0].buildings);
  for (const place of city.places ?? [])
    if (place.buildingId !== undefined && military.has(place.buildingId)) place.access = 'military';
  // The relief centre shares the occupied building shell, with actual supply
  // cabinets replacing four of its original ground-floor residential fixtures.
  for (const [index, place] of (city.places ?? [])
    .filter((p) => p.buildingId === layout.clinic && p.floor === 0 && p.kind === 'home')
    .slice(0, 4)
    .entries()) {
    const item = city.props?.find((p) => p.id === place.furnitureId);
    if (!item) continue;
    item.name = `Medical supplies ${index + 1}`;
    item.furniture = 'cabinet';
    furnish(item.cells[0], 'cabinet', index);
    place.kind = 'staff';
    place.name = `Medical supply inspection ${index + 1}`;
    place.pose = undefined;
    place.duration = 12;
  }
  layout.plant.duties = [];
  for (const building of [layout.plant.control, ...layout.plant.halls]) {
    const options = (city.places ?? []).filter(
      (p) =>
        p.buildingId === building &&
        p.floor === 0 &&
        ['desk', 'workbench', 'maintenance', 'seat'].includes(p.kind),
    );
    for (let i = 0; i < Math.min(2, Math.floor(options.length / 2)); i++) {
      const places = options.slice(i * 2, i * 2 + 2);
      for (const p of places) p.access = 'staff';
      layout.plant.duties.push({ building, places });
    }
  }
  return city;
}
